import React, { useEffect, useMemo } from 'react'
import { Col, Container, Form, Row, Spinner } from 'react-bootstrap'
import { Controller, useForm } from 'react-hook-form'

import { ScopedBlock } from '../../../components/ScopedBlock/ScopedBlock'
import { MissingScopeAction } from '../../../components/ScopedBlock/models'
import { FormSaveBar } from '../../../components/FormSaveBar/FormSaveBar'

import { COMBINED_ROLE_GROUPS, getCoveredRoleGroups, mapPermissionsByGroup, PERMISSION_GROUP_LABELS, PERMISSION_GROUPS, PermissionGroupKey } from './utils'

import { useAppDispatch, useAppSelector } from '../../../store/hooks'
import { selectAccountId } from '../../../store/auth/authSlice'
import { OAuth2App } from '../../../store/auth/auth'
import { useListPermissionsQuery } from '../../../store/permissions/permissions'

import './OAuth2AppsPermissionsTab.scss'
import { useZocksQuota } from '../../../components/ScopedBlock/hooks'
import { useOAuth2AppRolePermissions } from './hooks'
import { useGetRolesQuery } from '../../../store/role/role'
import { addToast } from '../../../store/toast/toastSlice'

type PermissionAccess = 'read' | 'write'

type FormValues = {
  roleName: string
}

const READ_SCOPES = ['list', 'get', 'read']

const getPermissionAccess = (scope: string): PermissionAccess => {
  const [, action] = scope.split(':')
  return READ_SCOPES.includes(action) ? 'read' : 'write'
}

interface PermissionAccessBadgeProps {
  access: PermissionAccess
}

const PermissionAccessBadge: React.FC<PermissionAccessBadgeProps> = ({ access }) => (
  <div
    className={`d-flex py-1 px-2 justify-content-center align-items-center zocks-gap-0.25 zocks-border-0.5 ${
      access === 'read' ? 'zocks-text-primary-dark zocks-bg-color-primary-light' : 'zocks-text-warning-text zocks-bg-color-warning'
    }`}
  >
    {access === 'read' ? 'Read' : 'Write'}
  </div>
)

interface OAuth2AppsPermissionRowProps {
  scope: string
  description: string
}

const OAuth2AppsPermissionRow: React.FC<OAuth2AppsPermissionRowProps> = ({ scope, description }) => (
  <Row className='apps-permissions-row-box py-3 px-4 mx-0 my-3 zocks-gap-2'>
    <Col xs={9} className='d-flex flex-column gap-1 p-0'>
      <div className='h6'>{description}</div>
      <div className='label-medium zocks-text-gray-600'>{scope}</div>
    </Col>
    <Col className='d-flex flex-column justify-content-center align-items-end p-0'>
      <PermissionAccessBadge access={getPermissionAccess(scope)} />
    </Col>
  </Row>
)

export interface OAuth2AppsPermissionsTabProps {
  oAuth2App: OAuth2App
}

export const OAuth2AppsPermissionsTab: React.FC<OAuth2AppsPermissionsTabProps> = ({ oAuth2App }) => {
  const { scopes, permissions, id: appId, clientId } = oAuth2App
  const accountId = useAppSelector(selectAccountId)
  const dispatch = useAppDispatch()

  const initialSelectedRadioButton = COMBINED_ROLE_GROUPS[0].name

  const form = useForm<FormValues>({
    defaultValues: { roleName: initialSelectedRadioButton },
    mode: 'onChange',
  })

  const { control, handleSubmit, reset, formState } = form
  const { errors, isDirty, isSubmitting, isValid } = formState

  const { data: permissionsData, isLoading: permissionsIsLoading } = useListPermissionsQuery()

  const {
    data: rolesResponse,
    isLoading: isLoadingRoles,
    isFetching: isFetchingRoles,
  } = useGetRolesQuery(
    {
      accountId,
      pageRequest: { page: 0, size: 500 },
    },
    {
      refetchOnMountOrArgChange: true,
    },
  )

  const { updatePermissions, isUpdating } = useOAuth2AppRolePermissions({
    accountId,
    appId,
    accountEnabled: permissions.account,
    successToast: {
      title: 'Success',
      messages: ['Successfully updated.'],
    },
    errorToast: {
      messages: ['Failed to update app. Please try again.'],
    },
  })

  const initialRoleName = useMemo(() => {
    const enabledSystemRoleIds = Object.entries(permissions.systemRoles)
      .filter(([, value]) => value)
      .map(([key]) => key)

    return getCoveredRoleGroups(enabledSystemRoleIds).find((roleGroup) => roleGroup.name)?.name ?? initialSelectedRadioButton
  }, [permissions.systemRoles, initialSelectedRadioButton])

  const filteredPermissions = useMemo(() => {
    if (!permissionsData?.data) {
      return []
    }
    const scopeSet = new Set(scopes)
    return permissionsData.data.filter((permission) => scopeSet.has(permission.id))
  }, [permissionsData, scopes])

  const groupedPermissions = useMemo(() => mapPermissionsByGroup(filteredPermissions), [filteredPermissions])

  const { hasQuota, validateQuotaValue } = useZocksQuota()
  const oauth2Enabled = hasQuota('integration.oauth2Enabled.value')
  const appNotBlacklisted = validateQuotaValue('integration.oauth2AppBlacklist', clientId)

  useEffect(() => {
    reset(
      { roleName: initialRoleName ?? initialSelectedRadioButton },
      {
        keepDirty: false,
        keepTouched: false,
      },
    )
  }, [initialRoleName, reset, initialSelectedRadioButton])

  const handleDiscard = () => {
    reset({ roleName: initialRoleName ?? initialSelectedRadioButton })
  }

  const onSubmit = async (values: FormValues) => {
    if (!rolesResponse) {
      dispatch(
        addToast({
          variant: 'error',
          title: 'Error',
          messages: ['Failed to update app. Please try again.'],
        }),
      )
      return
    }
    const roleIds = COMBINED_ROLE_GROUPS.find((roleGroup) => roleGroup.name === values.roleName)?.roleIds
    const result = await updatePermissions({
      selectedRoleIds: roleIds ?? [],
      allSystemRoles: rolesResponse.data.map((role) => role.id),
    })
    if (!result.ok) {
      return result
    }
    reset(values)
    return result
  }

  return (
    <Container fluid className='px-0'>
      <div className='flex-grow-1 zocks-pb-10.0'>
        <ScopedBlock
          scope={{
            scope: 'oauth2_app:update',
            superScope: ['oauth2_app:update'],
          }}
          missingScopeAction={MissingScopeAction.HIDE}
        >
          {oauth2Enabled && appNotBlacklisted && (
            <Form id='app-role-edit-form' onSubmit={handleSubmit(onSubmit)}>
              <Row className='m-0 edit-roles-box gap-4 mb-4'>
                <Row className='p-0'>
                  <h4>Roles with access</h4>
                </Row>

                <Row className='p-0 m-0 gap-2'>
                  <Row className='p-0 m-0'>
                    <Col className='p-0 m-0'>
                      <Controller
                        name='roleName'
                        control={control}
                        render={({ field }) => (
                          <>
                            {COMBINED_ROLE_GROUPS.map((group) => (
                              <Form.Check
                                key={group.name}
                                disabled={!permissions.account}
                                inline
                                type='radio'
                                className='pb-2'
                                label={group.name}
                                id={`modal_${group.name}`}
                                {...field}
                                value={group.name}
                                checked={field.value === group.name}
                              />
                            ))}
                          </>
                        )}
                      />
                    </Col>
                  </Row>
                </Row>
              </Row>
            </Form>
          )}
        </ScopedBlock>

        <Row className='mx-0'>
          <Col className='p-0 my-0'>
            <Row className='mb-3'>
              <h4>Permissions</h4>
            </Row>

            {permissionsIsLoading ? (
              <div className='d-flex align-items-center justify-content-center'>
                <Spinner />
              </div>
            ) : (
              (Object.keys(PERMISSION_GROUPS) as PermissionGroupKey[]).map((groupKey) => {
                const permissionsInGroup = groupedPermissions[groupKey]
                if (!permissionsInGroup.length) {
                  return null
                }
                return (
                  <div key={groupKey} className='mb-4'>
                    <h6 className='mb-3'>{PERMISSION_GROUP_LABELS[groupKey]}</h6>

                    {permissionsInGroup.map((permission) => (
                      <OAuth2AppsPermissionRow key={permission.id} scope={permission.id} description={permission.description} />
                    ))}
                  </div>
                )
              })
            )}
          </Col>
        </Row>
      </div>
      <FormSaveBar
        formId='app-role-edit-form'
        isVisible={isDirty}
        isSubmitting={isSubmitting || isUpdating}
        isValid={isValid && !isLoadingRoles && !isFetchingRoles && !!rolesResponse}
        isDirty={isDirty}
        errors={errors}
        onDiscard={handleDiscard}
        discardLabel={'Discard changes'}
        saveLabel={'Save changes'}
        defaultFormLabel={'You have unsaved changes.'}
      />
    </Container>
  )
}
